import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, Loader2, Clock, CheckCircle2, XCircle, Send, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useLanguage } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { Skeleton } from '../components/ui/skeleton';

export default function BecomeCoach() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, checkAuth } = useAuth();
  const [state, setState] = useState(null); // {request, role}
  const [bio, setBio] = useState('');
  const [specialties, setSpecialties] = useState('');
  const [links, setLinks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/academy/coach-requests/me');
      setState(data);
    } catch {
      setState({ request: null, role: user?.role || 'player' });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post('/academy/coach-requests', { bio: bio.trim(), specialties: specialties.trim(), links: links.trim() });
      toast.success(t('submitApplication') + ' \u2713');
      await load();
      await checkAuth();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'object' ? JSON.stringify(detail) : detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  if (state === null) {
    return (
      <div className="max-w-xl mx-auto pt-2 space-y-3" data-testid="become-coach-loading">
        <Skeleton className="h-40 w-full bg-white/[0.05] rounded-[20px]" />
      </div>
    );
  }

  const role = state.role || user?.role || 'player';
  const pending = state.request?.status === 'pending';
  const rejected = state.request?.status === 'rejected';

  if (role === 'coach' || role === 'admin') {
    return (
      <div className="max-w-xl mx-auto pt-2" data-testid="become-coach-page">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="ss-card p-8 text-center space-y-4" data-testid="become-coach-approved">
          <span className="inline-flex w-14 h-14 rounded-[16px] bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.3)] items-center justify-center">
            <CheckCircle2 size={26} className="text-[#22C55E]" />
          </span>
          <h1 className="font-display font-bold text-xl text-white">{t('applicationApproved')}</h1>
          <button onClick={() => navigate('/coach')} data-testid="become-coach-go-studio" className="ss-btn-primary h-11 px-6 text-sm font-bold font-display inline-flex items-center gap-2">
            <GraduationCap size={16} /> {t('coachArea')} <ChevronRight size={14} />
          </button>
        </motion.div>
      </div>
    );
  }

  if (pending) {
    return (
      <div className="max-w-xl mx-auto pt-2" data-testid="become-coach-page">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="ss-card p-8 text-center space-y-4" data-testid="become-coach-pending">
          <span className="inline-flex w-14 h-14 rounded-[16px] bg-[rgba(233,196,106,0.1)] border border-[rgba(233,196,106,0.3)] items-center justify-center">
            <Clock size={26} className="text-[#E9C46A]" />
          </span>
          <h1 className="font-display font-bold text-xl text-white">{t('becomeCoach')}</h1>
          <p className="text-sm text-white/60">{t('applicationPending')}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto pt-2 space-y-4" data-testid="become-coach-page">
      <div>
        <h1 className="font-display font-bold text-2xl tracking-tight text-white">{t('becomeCoach')}</h1>
        <p className="text-sm text-white/55 mt-1">{t('becomeCoachSub')}</p>
      </div>

      {rejected && (
        <div className="flex items-start gap-2.5 text-xs text-[#E9C46A] bg-[rgba(233,196,106,0.07)] border border-[rgba(233,196,106,0.25)] rounded-[14px] px-3.5 py-3" data-testid="become-coach-rejected-note">
          <XCircle size={15} className="shrink-0 mt-0.5" /> {t('applicationRejected')}
        </div>
      )}

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="ss-card p-5 space-y-4" data-testid="become-coach-form">
        <div className="space-y-1.5">
          <label className="font-mono text-[11px] uppercase tracking-wider text-white/45">{t('coachBio')}</label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder={t('coachBioPlaceholder')}
            rows={4}
            maxLength={1000}
            data-testid="become-coach-bio"
            className="w-full rounded-[14px] bg-white/[0.05] border border-white/10 p-3.5 text-sm text-white placeholder:text-white/30 resize-none focus:outline-none"
          />
        </div>
        <div className="space-y-1.5">
          <label className="font-mono text-[11px] uppercase tracking-wider text-white/45">{t('specialties')}</label>
          <input
            value={specialties}
            onChange={(e) => setSpecialties(e.target.value)}
            placeholder={t('specialtiesPlaceholder')}
            maxLength={300}
            data-testid="become-coach-specialties"
            className="w-full h-11 rounded-[14px] bg-white/[0.05] border border-white/10 px-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none"
          />
        </div>
        <div className="space-y-1.5">
          <label className="font-mono text-[11px] uppercase tracking-wider text-white/45">{t('linksLabel')}</label>
          <input
            value={links}
            onChange={(e) => setLinks(e.target.value)}
            maxLength={300}
            data-testid="become-coach-links"
            className="w-full h-11 rounded-[14px] bg-white/[0.05] border border-white/10 px-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none"
          />
        </div>
        <button
          onClick={submit}
          disabled={submitting || bio.trim().length < 10}
          data-testid="become-coach-submit"
          className="ss-btn-primary w-full h-12 text-sm font-bold font-display flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {submitting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {t('submitApplication')}
        </button>
      </motion.div>
    </div>
  );
}
